import { FormEvent, useMemo, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { Button, Input, StatusMessage, Textarea } from "../components/ui";
import { apiUrl } from "../lib/api";
import { useLocalization } from "../lib/i18n";

type SupportCategory = "QUESTION" | "REQUEST" | "BUG" | "OTHER";

type SupportFormState = {
  name: string;
  email: string;
  category: SupportCategory;
  subject: string;
  message: string;
};

const INITIAL_FORM: SupportFormState = {
  name: "",
  email: "",
  category: "QUESTION",
  subject: "",
  message: "",
};

const supportCopy = {
  en: {
    title: "Support",
    intro:
      "SportEx100 is operated by SportEx100 Technologies Inc. Send a question or request and we'll follow up by email.",
    organizationLabel: "Organization",
    supportEmailLabel: "Support email",
    nameLabel: "Name (optional)",
    namePlaceholder: "Your name",
    emailLabel: "Email",
    emailPlaceholder: "name@example.com",
    typeLabel: "Type",
    typeOptions: {
      QUESTION: "Question",
      REQUEST: "Request",
      BUG: "Bug",
      OTHER: "Other",
    },
    subjectLabel: "Subject",
    subjectPlaceholder: "Short summary",
    messageLabel: "Message",
    messagePlaceholder: "Tell us what you need help with.",
    submit: "Submit Request",
    submitting: "Submitting...",
    submittedWithId: "Thanks. Your request was submitted with ID {requestId}.",
    submitted: "Thanks. Your request was submitted.",
    submitError: "Unexpected error while submitting support request.",
    fallbackError: "Could not submit your request. Please try again.",
  },
  fr: {
    title: "Assistance",
    intro:
      "SportEx100 est exploité par SportEx100 Technologies Inc. Envoyez-nous une question ou une demande et nous vous répondrons par courriel.",
    organizationLabel: "Organisation",
    supportEmailLabel: "Courriel d'assistance",
    nameLabel: "Nom (facultatif)",
    namePlaceholder: "Votre nom",
    emailLabel: "Courriel",
    emailPlaceholder: "nom@example.com",
    typeLabel: "Type",
    typeOptions: {
      QUESTION: "Question",
      REQUEST: "Demande",
      BUG: "Problème",
      OTHER: "Autre",
    },
    subjectLabel: "Sujet",
    subjectPlaceholder: "Bref résumé",
    messageLabel: "Message",
    messagePlaceholder: "Dites-nous ce dont vous avez besoin.",
    submit: "Envoyer la demande",
    submitting: "Envoi...",
    submittedWithId: "Merci. Votre demande a été envoyée avec l'identifiant {requestId}.",
    submitted: "Merci. Votre demande a été envoyée.",
    submitError: "Erreur inattendue lors de l'envoi de la demande d'assistance.",
    fallbackError: "Impossible d'envoyer votre demande. Veuillez réessayer.",
  },
  ja: {
    title: "サポート",
    intro:
      "SportEx100 は SportEx100 Technologies Inc. が運営しています。ご質問やご依頼を送信してください。メールで返信します。",
    organizationLabel: "運営会社",
    supportEmailLabel: "サポートメール",
    nameLabel: "氏名（任意）",
    namePlaceholder: "お名前",
    emailLabel: "メール",
    emailPlaceholder: "name@example.com",
    typeLabel: "種類",
    typeOptions: {
      QUESTION: "質問",
      REQUEST: "依頼",
      BUG: "不具合",
      OTHER: "その他",
    },
    subjectLabel: "件名",
    subjectPlaceholder: "短い概要",
    messageLabel: "メッセージ",
    messagePlaceholder: "サポートが必要な内容をお知らせください。",
    submit: "リクエストを送信",
    submitting: "送信中...",
    submittedWithId: "ありがとうございます。リクエスト ID {requestId} で送信されました。",
    submitted: "ありがとうございます。リクエストが送信されました。",
    submitError: "サポートリクエストの送信中に予期しないエラーが発生しました。",
    fallbackError: "リクエストを送信できませんでした。もう一度お試しください。",
  },
  es: {
    title: "Soporte",
    intro:
      "SportEx100 es operado por SportEx100 Technologies Inc. Envía una pregunta o solicitud y te responderemos por email.",
    organizationLabel: "Organización",
    supportEmailLabel: "Email de soporte",
    nameLabel: "Nombre (opcional)",
    namePlaceholder: "Tu nombre",
    emailLabel: "Email",
    emailPlaceholder: "nombre@example.com",
    typeLabel: "Tipo",
    typeOptions: {
      QUESTION: "Pregunta",
      REQUEST: "Solicitud",
      BUG: "Error",
      OTHER: "Otro",
    },
    subjectLabel: "Asunto",
    subjectPlaceholder: "Resumen breve",
    messageLabel: "Mensaje",
    messagePlaceholder: "Cuéntanos con qué necesitas ayuda.",
    submit: "Enviar solicitud",
    submitting: "Enviando...",
    submittedWithId: "Gracias. Tu solicitud se envió con el ID {requestId}.",
    submitted: "Gracias. Tu solicitud se envió.",
    submitError: "Error inesperado al enviar la solicitud de soporte.",
    fallbackError: "No se pudo enviar tu solicitud. Inténtalo de nuevo.",
  },
} as const;

function formatSupportMessage(template: string, requestId: string) {
  return template.replace("{requestId}", requestId);
}

export function SupportPage() {
  const { user } = useUser();
  const { locale } = useLocalization();
  const copy = supportCopy[locale] ?? supportCopy.en;
  const [form, setForm] = useState<SupportFormState>(() => ({
    ...INITIAL_FORM,
    name: user?.fullName ?? "",
    email: user?.primaryEmailAddress?.emailAddress ?? "",
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const canSubmit = useMemo(() => {
    return (
      form.email.trim().length > 0 &&
      form.subject.trim().length >= 3 &&
      form.message.trim().length >= 10
    );
  }, [form]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const response = await fetch(apiUrl("/api/support/requests"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: form.name.trim() || null,
          email: form.email.trim(),
          category: form.category,
          subject: form.subject.trim(),
          message: form.message.trim(),
          user_id: user?.id ?? null,
        }),
      });

      let payload: { request_id?: string; detail?: string | { message?: string } } | null = null;
      try {
        payload = await response.json();
      } catch {
        payload = null;
      }

      if (!response.ok) {
        const detail = payload?.detail;
        const errorMessage =
          typeof detail === "string"
            ? detail
            : detail?.message || copy.fallbackError;
        throw new Error(errorMessage);
      }

      const requestId = payload?.request_id;
      setSubmitSuccess(
        requestId
          ? formatSupportMessage(copy.submittedWithId, requestId)
          : copy.submitted
      );
      setForm((prev) => ({
        ...INITIAL_FORM,
        name: prev.name,
        email: prev.email,
      }));
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : copy.submitError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 md:gap-6 px-4 py-4 md:px-0">
      <section className="rounded-card border border-border-subtle bg-background-card p-4 shadow-card md:p-6">
        <h1 className="text-title-sm md:text-title-md font-bold tracking-tight text-foreground">{copy.title}</h1>
        <p className="mt-2 text-body-sm text-foreground-muted">{copy.intro}</p>
        <div className="mt-4 grid gap-3 rounded-lg border border-border-subtle bg-background-raised p-4 text-body-sm text-foreground md:grid-cols-2">
          <div>
            <p className="font-semibold text-foreground-muted">{copy.organizationLabel}</p>
            <p className="mt-1 font-semibold">SportEx100 Technologies Inc.</p>
          </div>
          <div>
            <p className="font-semibold text-foreground-muted">{copy.supportEmailLabel}</p>
            <a className="mt-1 block font-semibold text-brand hover:text-brand-hover" href="mailto:info@sportex100.com">
              info@sportex100.com
            </a>
          </div>
        </div>
      </section>

      <section className="rounded-card border border-border-subtle bg-background-card p-4 shadow-card md:p-6">
        {submitError && (
          <StatusMessage variant="error" className="mb-4">
            {submitError}
          </StatusMessage>
        )}
        {submitSuccess && (
          <StatusMessage variant="success" className="mb-4">
            {submitSuccess}
          </StatusMessage>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-2">
              <span className="text-body-sm font-semibold text-foreground">{copy.nameLabel}</span>
              <Input
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder={copy.namePlaceholder}
                maxLength={120}
                autoComplete="name"
              />
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-body-sm font-semibold text-foreground">{copy.emailLabel}</span>
              <Input
                type="email"
                value={form.email}
                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                placeholder={copy.emailPlaceholder}
                maxLength={254}
                autoComplete="email"
                required
              />
            </label>
          </div>

          <label className="flex flex-col gap-2">
            <span className="text-body-sm font-semibold text-foreground">{copy.typeLabel}</span>
            <select
              value={form.category}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, category: event.target.value as SupportCategory }))
              }
              className="h-10 rounded-input border border-border-subtle bg-background-raised px-3 text-body-sm text-foreground outline-none transition-all duration-150 focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand"
            >
              <option value="QUESTION">{copy.typeOptions.QUESTION}</option>
              <option value="REQUEST">{copy.typeOptions.REQUEST}</option>
              <option value="BUG">{copy.typeOptions.BUG}</option>
              <option value="OTHER">{copy.typeOptions.OTHER}</option>
            </select>
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-body-sm font-semibold text-foreground">{copy.subjectLabel}</span>
            <Input
              value={form.subject}
              onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
              placeholder={copy.subjectPlaceholder}
              maxLength={150}
              required
            />
          </label>

          <label className="flex flex-col gap-2">
            <span className="text-body-sm font-semibold text-foreground">{copy.messageLabel}</span>
            <Textarea
              value={form.message}
              onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
              placeholder={copy.messagePlaceholder}
              className="min-h-[140px]"
              maxLength={4000}
              required
            />
          </label>

          <div className="flex items-center justify-end">
            <Button type="submit" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? copy.submitting : copy.submit}
            </Button>
          </div>
        </form>
      </section>
    </div>
  );
}
